import { DatePicker } from 'components/DatePicker'
import TimePicker, { TimeZone } from 'components/TimePicker'
import { TooltipLabel } from 'components/TooltipLabel'
import dayjs from 'dayjs'
import React from 'react'
import { Dropdown, Form, Input } from 'semantic-ui-react'

export default function ExpirationSettings({
  settings,
  onToggleEnabled,
  onChangeDuration,
  onChangeUnit,
  timeUntilExpiration,
  onChangeTimeUntilExpiration,
  expirationUnit,
  onChangeExpirationUnit,
  isExpirationEnabled,
  onChangeIsExpirationEnabled,
  canSetPostSurveyEmailExpiration,
  isClosedEnabled,
  onChangeIsClosedEnabled,
  scheduleDate,
  setScheduleDate,
  scheduleTime,
  setScheduleTime,
}) {
  const postEmailSent = settings?.find((item) => item.type === 'post_email_sent')
  const postEmailSentChecked = postEmailSent?.enabled || false

  const isScheduleDateMatchToday = !scheduleDate?.isAfter(dayjs())

  const durations = [
    {
      key: 'hours',
      text: 'hours',
      value: 'hours',
    },
    {
      key: 'days',
      text: 'days',
      value: 'days',
    },
    {
      key: 'weeks',
      text: 'weeks',
      value: 'weeks',
    },
    {
      key: 'months',
      text: 'months',
      value: 'months',
    },
  ]

  function avoidLeadingZero(value) {
    if (value.length > 1 && value.startsWith('0')) {
      return value.slice(1)
    } else {
      return value
    }
  }
  return (
    <React.Fragment>
      <h2>Expiration settings</h2>

      <Form>
        <Form.Field className="block expiration-field-group !flex-col !items-start">
          <Form.Group>
            <Form.Checkbox
              className="!pr-0"
              label="Lock responses from being updated"
              checked={isExpirationEnabled}
              onChange={(_, { checked }) => {
                onChangeIsExpirationEnabled(checked)
              }}
            />
            <TooltipLabel
              label=""
              descriptionTitle="Lock editing after submission"
              descriptionContent="People can edit their answers at any time unless you lock them. This setting locks edits a set time after someone first submits a response."
              learnMoreLink="https://help.simplesat.io/en/articles/7171943-survey-settings#lock-responses-from-being-updated"
            />
          </Form.Group>
          {isExpirationEnabled && (
            <Form.Group inline className="!flex-row !mb-0 sub-menu-indent">
              <label>Lock edits after:</label>
              <Form.Field>
                <Input
                  label={
                    <Dropdown
                      options={durations}
                      value={expirationUnit ? expirationUnit : 'hours'}
                      onChange={(_, { value }) => {
                        onChangeExpirationUnit(value)
                      }}
                    />
                  }
                  className="number-field"
                  labelPosition="right"
                  value={parseInt(timeUntilExpiration || 0)}
                  type="text"
                  pattern="[0-9]*"
                  onChange={(event, { value }) => {
                    if (event.target.validity.valid) {
                      const newValue = avoidLeadingZero(value)
                      onChangeTimeUntilExpiration(newValue || 0)
                    }
                  }}
                />
              </Form.Field>
              <span>they're first created.</span>
            </Form.Group>
          )}
        </Form.Field>

        {canSetPostSurveyEmailExpiration && (
          <Form.Field className="expiration-field-group !flex-col !items-start">
            <Form.Group>
              <Form.Checkbox
                className="!pr-0"
                label="Prevent responses from being created"
                checked={postEmailSentChecked}
                onChange={(_, { checked }) => {
                  onToggleEnabled('post_email_sent', checked)
                }}
              />
              <TooltipLabel
                label=""
                descriptionTitle="Limit response window after email is sent"
                descriptionContentWithFormat={
                  <p>
                    By default, survey links never expire. Turn this on to limit how long someone
                    can respond <b>after the survey email is delivered</b>.
                  </p>
                }
                learnMoreLink="https://help.simplesat.io/en/articles/7171943-survey-settings#prevent-responses-from-being-created"
              />
            </Form.Group>
            {postEmailSentChecked && (
              <Form.Group inline className="!flex-row !mb-0 sub-menu-indent">
                <label>Allow responses for:</label>
                <Form.Field>
                  <Input
                    label={
                      <Dropdown
                        options={durations}
                        value={postEmailSent?.duration_unit || 'hours'}
                        onChange={(_, { value }) => {
                          onChangeUnit('post_email_sent', value)
                        }}
                      />
                    }
                    className="number-field"
                    labelPosition="right"
                    value={parseInt(postEmailSent?.duration_value || 0)}
                    type="text"
                    pattern="[0-9]*"
                    onChange={(event, { value }) => {
                      if (event.target.validity.valid) {
                        const newValue = avoidLeadingZero(value)
                        onChangeDuration('post_email_sent', newValue || 0)
                      }
                    }}
                  />
                </Form.Field>
                <span>after the survey email is sent.</span>
              </Form.Group>
            )}
          </Form.Field>
        )}

        <Form.Field className="expiration-field-group !flex-col !items-start">
          <Form.Group>
            <Form.Checkbox
              className="!pr-0"
              label="Close the survey on a specific date"
              checked={isClosedEnabled}
              onChange={(_, { checked }) => {
                onChangeIsClosedEnabled(checked)
              }}
            />
            <TooltipLabel
              label=""
              descriptionTitle="Close the survey on a specific date"
              descriptionContentWithFormat={
                <>
                  <p>Surveys stay open until you manually close them.</p>
                  <p>Turn this on to automatically close the survey at a specific date and time.</p>
                  <p>
                    You can also use it as an alternative to archiving. If you still want to access
                    the survey's dashboards, just set the date and time to now to close it
                    immediately.
                  </p>
                </>
              }
              learnMoreLink="https://help.simplesat.io/en/articles/7171943-survey-settings#close-the-survey-on-a-specific-date"
            />
          </Form.Group>
          {isClosedEnabled && (
            <>
              <Form.Group inline className="!flex-row !mb-0 sub-menu-indent">
                <label>Close survey on:</label>
                <DatePicker
                  className="!py-0.5 !pl-0"
                  scheduleDate={scheduleDate}
                  setScheduleDate={setScheduleDate}
                />
                <span className="m-2">at</span>
                <TimePicker
                  setScheduleTime={setScheduleTime}
                  scheduleTime={scheduleTime}
                  isScheduleDateMatchToday={isScheduleDateMatchToday}
                  allowLastHour={true}
                />
                <TimeZone className="!mt-0 !ml-2" />
              </Form.Group>
              <Form.Field className="!mt-2 !pl-0 sub-menu-indent">
                <span className="text-grey">
                  Set this to the previous hour to close the survey immediately while keeping the
                  dashboards access.
                </span>
              </Form.Field>
            </>
          )}
        </Form.Field>
      </Form>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/ExpirationSettings/index.js